import { useState, useEffect, useCallback } from "react"
import UserStatistics from "./user/UserStatistics"
import VIPAnalysis from "./user/VIPAnalysis"
import CohortRetention from "./user/CohortRetention"
import GlobalLoader from "@/components/Loading/GlobalLoader"
import styles from "../analytics.module.css"
import { api } from "@/utils/apiService"
import VipStatistics from "./user/VipStatistics"
import { useAuth } from "@/components/context/AuthContext"
import UserBehaviour from "./user/UserBehaviour"
import Profitability from "./user/Profitability"
import UserInsights from "./user/UserInsights"
import VipPurchases from "./user/VipPurchases"
import VipInsights from "./user/VipInsights"
import { useTheme } from "@/components/context/ThemeContext"

export default function UserAnalytics() {
	const {theme} = useTheme()
	const { token } = useAuth()
	const [data, setData] = useState(null)
	const [vipAnalysis, setVipAnalysis] = useState(null)
	const [cohortAnalysis, setCohortAnalysis] = useState(null)
	const [loading, setLoading] = useState(true)
	const [period, setPeriod] = useState("monthly")

	const fetchUserData = useCallback(async (period) => {
		setLoading(true)

		try {
			const res = await api.get("/admin/analytics/users/overview", {
				headers: {
					Authorization: `Bearer ${token}`
				},
				params: {
					period
				}
			})

			// console.log(res.data.data)
			setData(res.data.data)
		} catch (error) {
			console.log("Error Fetching User Analysis ::", error)
		} finally {
			setLoading(false)
		}
	}, [])

	const fetchVipAnalysis = useCallback(async (period) => {
		setLoading(true)

		try {
			const res = await api.get("/admin/analytics/users/vip-analysis", {
				headers: {
					Authorization: `Bearer ${token}`
				},
				params: {
					period
				}
			})

			// console.log(res.data.data)
			setVipAnalysis(res.data.data)
		} catch (error) {
			console.log("Error Fetching VIP Analysis ::", error)
		} finally {
			setLoading(false)
		}
	}, [])

	const fetchCohortAnalysis = useCallback(async (period) => {
		setLoading(true)
		const cohortType = period !== "daily" ? period : "";
		try {
			const res = await api.get("/admin/analytics/users/cohort-analysis", {
				headers: {
					Authorization: `Bearer ${token}`
				},
				params: {
					cohortType
				}
			})

			// console.log(res.data.data)
			setCohortAnalysis(res.data.data)
		} catch (error) {
			console.log("Error Fetching Cohort Analysis ::", error)
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		fetchUserData(period)
		fetchVipAnalysis(period)
		fetchCohortAnalysis(period)
	}, [period, fetchUserData, fetchVipAnalysis, fetchCohortAnalysis])

	return (
		<div className={`${styles.analyticsPage} ${theme==="dark"?"dark":""}`}>
			<div className={styles.header}>
				<h1>User Analytics</h1>
				<select value={period} onChange={(e) => setPeriod(e.target.value)}>
					<option value="daily">Daily</option>
					<option value="weekly">Weekly</option>
					<option value="monthly">Monthly</option>
				</select>
			</div>

			{loading ? (
				<GlobalLoader
					isLoading={loading}
					variant="spinner"
					message="Loading User Analytics"
					fullScreen={false}
				/>
			) : (
				<>
					<UserStatistics data={data?.userStatistics} />
					<VipStatistics data={data?.VipStatistics} />
					<UserBehaviour data={data?.userBehavior} />
					<Profitability data={data?.profitabilityByUserType} />
					<UserInsights data={data?.insights} />
					<VipPurchases data={vipAnalysis?.vipPurchases} />
					<VIPAnalysis data={vipAnalysis} />
					<VipInsights data={vipAnalysis} />
					<CohortRetention data={cohortAnalysis} />
				</>
			)}
		</div>
	)
}
